/**
 * Toast notification system.
 * Supports dismiss-by-ID, close button, ARIA live region.
 */
import { uid, escapeHtml } from '../utils/helpers.js';
import { t } from '../i18n/index.js';

let container = null;

function ensureContainer() {
  if (container && document.body.contains(container)) return container;
  container = document.createElement('div');
  container.className = 'toast-container';
  container.setAttribute('role', 'status');
  container.setAttribute('aria-live', 'polite');
  document.body.appendChild(container);
  return container;
}

/**
 * Show a toast notification.
 * @param {string} message
 * @param {'info'|'success'|'warning'|'error'} type
 * @param {number} duration — auto-dismiss in ms (0 = manual only)
 * @returns {string} toast ID (for programmatic dismissal)
 */
export function showToast(message, type = 'info', duration = 4000) {
  const cont = ensureContainer();
  const id = uid();

  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;
  toast.dataset.toastId = id;
  // escapeHtml is load-bearing: every showError/showSuccess call site in ~20
  // frontends funnels through here, and many pass err.message straight from
  // the API — which frequently echoes user input back. Combined with the
  // script-src 'unsafe-inline' still present in 13 apps, an unescaped message
  // made any input-reflecting endpoint an XSS sink.
  toast.innerHTML = `
    <span class="toast-message">${escapeHtml(message)}</span>
    <button class="toast-close" aria-label="${t('toast.dismiss')}">&times;</button>
  `;

  toast.querySelector('.toast-close').addEventListener('click', () => dismissToast(id));
  cont.appendChild(toast);

  if (duration > 0) {
    setTimeout(() => dismissToast(id), duration);
  }

  return id;
}

/**
 * Dismiss a toast by its ID.
 */
export function dismissToast(id) {
  const toast = document.querySelector(`[data-toast-id="${id}"]`);
  if (!toast) return;
  toast.style.opacity = '0';
  toast.style.transform = 'translateX(20px)';
  toast.style.transition = 'all 0.3s ease';
  setTimeout(() => toast.remove(), 300);
}

/* ── Convenience exports ── */
export function showSuccess(message, duration) { return showToast(message, 'success', duration); }
export function showError(message, duration) { return showToast(message, 'error', duration); }
export function showInfo(message, duration) { return showToast(message, 'info', duration); }
export function showWarning(message, duration) { return showToast(message, 'warning', duration); }

/** Object-style API: toast.success('Done!') */
export const toast = {
  info:    (msg, dur) => showToast(msg, 'info', dur),
  success: (msg, dur) => showToast(msg, 'success', dur),
  warning: (msg, dur) => showToast(msg, 'warning', dur),
  error:   (msg, dur) => showToast(msg, 'error', dur),
};
